import React, { useState, useCallback, useEffect, useContext, useRef } from 'react';
import ReactFlow, {
    addEdge,
    Background,
    Controls,
    applyEdgeChanges,
    applyNodeChanges,
    Panel,
    ReactFlowProvider
} from 'reactflow';
import 'reactflow/dist/style.css';
import { useParams, useNavigate } from 'react-router-dom';
import { api } from '../api/api';
import { SnackbarContext } from '../App';
import { TriggerNode, ConditionNode, ActionNode } from './automation/nodes';
import NodeSidebar from './AutomationBuilder.Sidebar';
import AutomationRuns from './AutomationRuns';
import RunInspector from './RunInspector';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { cn } from '../lib/utils';
import {
    ChevronLeft,
    Save,
    Rocket,
    Zap,
    Target,
    Play,
    History,
    ShieldAlert,
    Settings,
    Layout,
    Loader2,
    MessageSquare, MailWarning, ArrowRightCircle, Globe, RefreshCw, UserPlus, Ban, ChevronDown, Split, Mail, Tag, StickyNote, Activity, CheckSquare, Search, Filter, Clock
} from 'lucide-react';

const PIPELINE_CATEGORIES = [
    {
        id: 'triggers',
        type: 'triggerNode',
        label: 'Inbound Signal',
        subtitle: 'Start automations from incoming events.',
        icon: Zap,
        color: 'text-blue-500 bg-blue-500/10 border-blue-500/20',
        items: [
            { subtype: 'REPLY', label: 'Reply Received', icon: MessageSquare, desc: 'Fires when a contact replies to a thread.' },
            { subtype: 'BOUNCE', label: 'Campaign Bounce', icon: MailWarning, desc: 'Fires on hard or soft email bounces.' },
            { subtype: 'NO_REPLY', label: 'No Reply After Delay', icon: Clock, desc: 'Fires when a prospect goes silent.' },
            { subtype: 'AUTO_REPLY', label: 'Auto-Reply (OOO)', icon: ArrowRightCircle, desc: 'Fires on automated out-of-office.' },
        ]
    },
    {
        id: 'filters',
        type: 'conditionNode',
        label: 'Filter Path',
        subtitle: 'Branch logic using condition sets.',
        icon: Target,
        color: 'text-amber-500 bg-amber-500/10 border-amber-500/20',
        items: [
            { subtype: 'CONDITION', label: 'Ruleset Match', icon: Split, desc: 'Evaluate specific fields and properties.' }
        ]
    },
    {
        id: 'actions',
        type: 'actionNode',
        label: 'Automation Action',
        subtitle: 'Create tasks, send emails, or update CRM.',
        icon: Play,
        color: 'text-green-500 bg-green-500/10 border-green-500/20',
        items: [
            { subtype: 'SEND_EMAIL', label: 'Send Email Reply', icon: Mail, desc: 'Automate a contextual response.' },
            { subtype: 'TASK', label: 'Create Manual Task', icon: CheckSquare, desc: 'Schedule a follow-up action for ops.' },
            { subtype: 'WEBHOOK', label: 'Trigger Webhook', icon: Globe, desc: 'POST payload to an external endpoint.' },
            { subtype: 'SEQUENCE_ENROLLMENT', label: 'Enroll in Sequence', icon: RefreshCw, desc: 'Re-engage via automated campaign.' },
            { subtype: 'ASSIGN_OWNER', label: 'Assign Owner', icon: UserPlus, desc: 'Route contact to a specific rep.' },
            { subtype: 'STATUS_CHANGE', label: 'Change Status', icon: Activity, desc: 'Update lifecycle stage.' },
            { subtype: 'TAG', label: 'Add Tag', icon: Tag, desc: 'Apply a categorization label.' },
            { subtype: 'ADD_NOTE', label: 'Add Note', icon: StickyNote, desc: 'Log contextual history to timeline.' },
            { subtype: 'SUPPRESS', label: 'Add to Blocklist', icon: Ban, desc: 'Opt-out contact globally.' },
        ]
    }
];

const nodeTypes = {
    triggerNode: TriggerNode,
    conditionNode: ConditionNode,
    actionNode: ActionNode
};

const AutomationBuilderShell = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const showSnackbar = useContext(SnackbarContext);
    const reactFlowWrapper = useRef(null);
    
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [automation, setAutomation] = useState(null);
    const [reactFlowInstance, setReactFlowInstance] = useState(null);
    const [selectedNodeId, setSelectedNodeId] = useState(null);
    const [validationErrors, setValidationErrors] = useState([]);
    const [lastSaved, setLastSaved] = useState(null);
    const [viewMode, setViewMode] = useState('DESIGN'); // 'DESIGN' or 'EXECUTION'
    const [selectedRunId, setSelectedRunId] = useState(null);

    // Pipeline Library Rich Discoverability
    const [librarySearch, setLibrarySearch] = useState('');
    const [expandedLibraryCats, setExpandedLibraryCats] = useState(PIPELINE_CATEGORIES.map(c => c.id));

    const toggleLibraryCategory = (catId) => {
        setExpandedLibraryCats(prev => 
            prev.includes(catId) ? prev.filter(c => c !== catId) : [...prev, catId]
        );
    };

    const searchStr = librarySearch.toLowerCase();
    const filteredLibraryConfig = PIPELINE_CATEGORIES.map(cat => ({
        ...cat,
        items: cat.items.filter(item => 
            item.label.toLowerCase().includes(searchStr) || 
            item.desc.toLowerCase().includes(searchStr)
        )
    })).filter(cat => cat.items.length > 0 || cat.label.toLowerCase().includes(searchStr));

    const onNodesChange = useCallback(
        (changes) => setNodes((nds) => applyNodeChanges(changes, nds)),
        []
    );
    const onEdgesChange = useCallback(
        (changes) => setEdges((eds) => applyEdgeChanges(changes, eds)),
        []
    );
    const onConnect = useCallback(
        (params) => setEdges((eds) => addEdge({ ...params, animated: true, style: { strokeWidth: 2 } }, eds)),
        []
    );

    const validateFlow = useCallback(() => {
        const issues = [];
        const triggerNodes = nodes.filter(n => n.type === 'triggerNode');
        
        if (triggerNodes.length === 0) {
            issues.push({ id: 'no-trigger', message: 'Missing trigger node. Every flow needs a start.', severity: 'error' });
        }
        
        nodes.forEach(node => {
            if (node.type === 'actionNode') {
                const hasIncoming = edges.some(e => e.target === node.id);
                if (!hasIncoming) {
                    issues.push({ id: `orphan-${node.id}`, message: `Action "${node.data.label || 'Node'}" is disconnected.`, severity: 'warning', nodeId: node.id });
                }
            }
        });

        setValidationErrors(issues);
        return !issues.some(i => i.severity === 'error');
    }, [nodes, edges]);

    useEffect(() => {
        if (nodes.length > 0) validateFlow();
    }, [nodes, edges, validateFlow]);

    const fetchAutomation = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get(`/api/inbound-automations/${id}`);
            const data = res.data.data;
            setAutomation(data);
            setNodes(data.graph.nodes || []);
            setEdges(data.graph.edges || []);
            setLastSaved(new Date(data.updatedAt));
        } catch (err) {
            showSnackbar('Failed to load automation', 'error');
            navigate('/inbox/automations');
        } finally {
            setLoading(false);
        }
    }, [id, navigate, showSnackbar]);

    useEffect(() => {
        fetchAutomation();
    }, [fetchAutomation]);

    const handleSave = async () => {
        try {
            await api.put(`/api/inbound-automations/${id}`, {
                name: automation.name,
                graph: { nodes, edges }
            });
            showSnackbar('Draft saved', 'success');
            setLastSaved(new Date());
        } catch (err) {
            showSnackbar(err.message || 'Save failed', 'error');
        }
    };

    const handlePublish = async () => {
        if (!validateFlow()) {
            showSnackbar('Please fix critical errors before publishing', 'error');
            return;
        }

        try {
            // Save current graph state before publishing for consistency
            await api.put(`/api/inbound-automations/${id}`, {
                name: automation.name,
                graph: { nodes, edges }
            });

            await api.post(`/api/inbound-automations/${id}/publish`);
            showSnackbar('Flow published successfully', 'success');
            setAutomation({ ...automation, status: 'PUBLISHED' });
            setLastSaved(new Date());
        } catch (err) {
            showSnackbar(err.message || 'Publish failed', 'error');
        }
    };

    const onDragOver = useCallback((event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    }, []);

    const onDrop = useCallback(
        (event) => {
            event.preventDefault();
            const type = event.dataTransfer.getData('application/reactflow');
            if (!type || !reactFlowInstance) return;

            const position = reactFlowInstance.screenToFlowPosition({
                x: event.clientX,
                y: event.clientY,
            });

            let subtypeData = null;
            try {
                const payload = event.dataTransfer.getData('application/reactflow-subtype');
                if (payload) subtypeData = JSON.parse(payload);
            } catch (e) {
                console.warn("Failed to parse subtype from library payload", e);
            }

            const newNode = {
                id: `node_${Date.now()}`,
                type,
                position,
                data: { 
                    label: subtypeData?.label || '',
                    triggerType: type === 'triggerNode' ? (subtypeData?.subtype || 'REPLY') : 'REPLY',
                    actionType: type === 'actionNode' ? (subtypeData?.subtype || 'TASK') : 'TASK',
                    params: {}
                },
            };

            setNodes((nds) => nds.concat(newNode));
        },
        [reactFlowInstance]
    );

    if (loading) return (
        <div className="h-screen flex items-center justify-center bg-background">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
    );

    return (
        <div className="h-screen flex flex-col bg-background overflow-hidden font-sans">
            {/* Header */}
            <header className="h-16 border-b border-border/50 flex items-center justify-between px-6 bg-card/50 backdrop-blur-xl z-20 shrink-0">
                <div className="flex items-center gap-6">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/inbox/automations')} className="rounded-full hover:bg-muted/50 transition-all active:scale-90">
                        <ChevronLeft className="h-5 w-5" />
                    </Button>
                    
                    <div className="flex items-center gap-1 bg-muted/30 p-1 rounded-lg border border-border/50">
                        <Button 
                            variant={viewMode === 'DESIGN' ? 'secondary' : 'ghost'} 
                            size="sm" 
                            onClick={() => setViewMode('DESIGN')}
                            className="text-[10px] h-7 font-black px-4 gap-1.5 transition-all"
                        >
                            <Layout className="h-3.5 w-3.5" />
                            DESIGN
                        </Button>
                        <Button 
                            variant={viewMode === 'EXECUTION' ? 'secondary' : 'ghost'} 
                            size="sm" 
                            onClick={() => {
                                setViewMode('EXECUTION');
                                setSelectedNodeId(null);
                            }}
                            className="text-[10px] h-7 font-black px-4 gap-1.5 transition-all"
                        >
                            <History className="h-3.5 w-3.5" />
                            EXECUTION
                        </Button>
                    </div>

                    <div className="w-px h-4 bg-border/50 mx-1" />

                    <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => navigate('/inbox/automations/legacy')}
                        className="text-[9px] h-7 font-black opacity-60 hover:opacity-100 gap-1.5 uppercase tracking-widest"
                    >
                        <ShieldAlert className="h-3 w-3" />
                        Legacy Rules (Compatibility Mode)
                    </Button>

                    <div className="flex flex-col ml-6">
                        <div className="flex items-center gap-3">
                            <h1 className="text-lg font-black tracking-tight text-foreground">{automation?.name}</h1>
                            <Badge className={`${automation?.status === 'PUBLISHED' ? 'bg-green-500/10 text-green-600 border-green-500/20' : 'bg-amber-500/10 text-amber-600 border-amber-500/20'} text-[9px] px-2 py-0.5 font-black uppercase tracking-widest`}>
                                {automation?.status}
                            </Badge>
                        </div>
                        {lastSaved && (
                            <span className="text-[9px] text-muted-foreground font-bold uppercase tracking-tighter opacity-70">
                                Last saved {lastSaved.toLocaleTimeString()}
                            </span>
                        )}
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={handleSave}
                        className="text-[10px] h-9 font-bold px-4 gap-2 hover:bg-primary/5 border border-transparent hover:border-primary/20 transition-all"
                    >
                        <Save className="h-4 w-4" />
                        Save Draft
                    </Button>
                    
                    {automation?.status === 'DRAFT' ? (
                        <Button 
                            size="sm" 
                            onClick={handlePublish}
                            className="text-[10px] h-9 font-bold px-4 gap-2 bg-primary hover:bg-primary/90 shadow-lg shadow-primary/20 text-primary-foreground"
                        >
                            <Rocket className="h-4 w-4" />
                            Publish Automation
                        </Button>
                    ) : (
                        <Button 
                            variant="secondary"
                            size="sm" 
                            onClick={() => api.put(`/api/inbound-automations/${automation?._id}/status`, { status: 'DRAFT' }).then(() => fetchAutomation())}
                            className="text-[10px] h-9 font-bold px-4 gap-2"
                        >
                            <Settings className="h-4 w-4" />
                            Return to Draft
                        </Button>
                    )}
                </div>
            </header>

            <main className="flex-1 relative flex overflow-hidden">
                {viewMode === 'DESIGN' ? (
                    <ReactFlowProvider>
                        <div className="flex-1 relative" ref={reactFlowWrapper}>
                            <ReactFlow
                                nodes={nodes}
                                edges={edges}
                                onNodesChange={onNodesChange}
                                onEdgesChange={onEdgesChange}
                                onConnect={onConnect}
                                onInit={setReactFlowInstance}
                                nodeTypes={nodeTypes}
                                onNodeClick={(_, node) => setSelectedNodeId(node.id)}
                                onPaneClick={() => setSelectedNodeId(null)}
                                onDrop={onDrop}
                                onDragOver={onDragOver}
                                fitView
                            >
                                <Background variant="dots" gap={24} size={1} color="rgba(0,0,0,0.1)" />
                                <Controls position="bottom-right" className="!bg-card !border-border !shadow-2xl !rounded-2xl" />
                                
                                <Panel position="top-left" className="bg-card backdrop-blur-3xl border border-border/50 rounded-3xl shadow-2xl m-6 flex flex-col w-[320px] ring-1 ring-black/5 max-h-[80vh] overflow-hidden">
                                     <div className="p-5 border-b border-border/50 shrink-0 bg-background/50">
                                         <h3 className="text-[10px] font-black uppercase tracking-widest text-muted-foreground/80 mb-3 flex items-center justify-between">
                                            Pipeline Library
                                            <Badge variant="outline" className="text-[8px] bg-primary/5 text-primary border-primary/20">{nodes.length} Nodes</Badge>
                                         </h3>
                                         <div className="relative">
                                             <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                             <Input 
                                                 placeholder="Search actions & triggers..." 
                                                 className="h-8 pl-8 text-xs bg-muted/30 border-border/50 focus-visible:ring-1 focus-visible:ring-primary/30 rounded-lg w-full font-medium"
                                                 value={librarySearch}
                                                 onChange={e => setLibrarySearch(e.target.value)}
                                             />
                                         </div>
                                     </div>
                                     
                                     <div className="flex-1 p-3 overflow-y-auto scrollbar-hide">
                                         {filteredLibraryConfig.length === 0 ? (
                                             <div className="py-8 text-center text-muted-foreground flex flex-col items-center">
                                                 <Filter className="w-8 h-8 opacity-20 mb-2" />
                                                 <p className="text-xs font-semibold">No capabilities match your search.</p>
                                             </div>
                                         ) : (
                                             <div className="space-y-3 pb-4">
                                                 {filteredLibraryConfig.map(cat => {
                                                     const isExpanded = expandedLibraryCats.includes(cat.id);
                                                     return (
                                                         <div key={cat.id} className={cn(
                                                             "rounded-2xl border bg-background/50 overflow-hidden transition-all shadow-sm",
                                                             isExpanded ? "border-border/80 ring-1 ring-primary/5 shadow-md" : "border-border/40 hover:border-border/80"
                                                         )}>
                                                             <button 
                                                                 className="w-full flex items-start gap-3 p-4 text-left hover:bg-muted/40 transition-colors group"
                                                                 onClick={() => toggleLibraryCategory(cat.id)}
                                                             >
                                                                 <div className={cn("p-1.5 rounded-lg shrink-0 border border-transparent shadow-sm", cat.color.replace('text-', 'text-').replace('border-', 'border-').split(' ')[1])}>
                                                                     <cat.icon className={cn("w-4 h-4", cat.color.split(' ')[0])} />
                                                                 </div>
                                                                 <div className="flex-1 min-w-0 pr-2">
                                                                     <div className="flex items-center justify-between mb-0.5">
                                                                         <span className="text-sm font-black tracking-tight text-foreground">{cat.label}</span>
                                                                         <ChevronDown className={cn("w-4 h-4 text-muted-foreground transition-transform duration-300", isExpanded ? "rotate-180" : "rotate-0 text-muted-foreground/40")} />
                                                                     </div>
                                                                     <p className="text-[10px] text-muted-foreground/80 font-semibold leading-relaxed line-clamp-2 pr-4">{cat.subtitle}</p>
                                                                 </div>
                                                             </button>
                                                             
                                                             <div className={cn(
                                                                 "grid transition-all duration-300 ease-in-out",
                                                                 isExpanded ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                                                             )}>
                                                                 <div className="overflow-hidden">
                                                                     <div className="p-3 pt-0 space-y-2 border-t border-border/30 bg-muted/10">
                                                                         {cat.items.map(item => (
                                                                             <DraggableComponent key={item.subtype} item={item} parentType={cat.type} />
                                                                         ))}
                                                                     </div>
                                                                 </div>
                                                             </div>
                                                         </div>
                                                     );
                                                 })}
                                             </div>
                                         )}
                                     </div>
                                 </Panel>

                                {validationErrors.length > 0 && (
                                    <Panel position="top-center" className="m-4">
                                        <div className={`flex items-center gap-3 px-6 py-3 rounded-2xl border shadow-2xl backdrop-blur-xl transition-all ${validationErrors.some(e => e.severity === 'error') ? 'bg-red-500/5 border-red-500/20 text-red-600' : 'bg-amber-500/5 border-amber-500/20 text-amber-600'}`}>
                                            <ShieldAlert className="h-5 w-5" />
                                            <p className="text-sm font-bold truncate max-w-md">
                                                {validationErrors[0].message}
                                            </p>
                                        </div>
                                    </Panel>
                                )}
                            </ReactFlow>
                        </div>
                        
                        <div 
                            className={cn(
                                "border-border/50 bg-card/50 backdrop-blur-3xl transition-all duration-500 ease-in-out shrink-0 relative overflow-hidden",
                                selectedNodeId ? "w-[320px] border-l opacity-100" : "w-0 border-none opacity-0"
                            )}
                        >
                            <div className="w-[320px] h-full absolute top-0 right-0">
                                {selectedNodeId ? (
                                    <NodeSidebar 
                                        node={nodes.find(n => n.id === selectedNodeId)} 
                                        onUpdate={(data) => {
                                            setNodes(nds => nds.map(n => n.id === selectedNodeId ? { ...n, data: { ...n.data, ...data } } : n));
                                        }}
                                        onDelete={() => {
                                            setNodes(nds => nds.filter(n => n.id !== selectedNodeId));
                                            setEdges(eds => eds.filter(e => e.source !== selectedNodeId && e.target !== selectedNodeId));
                                            setSelectedNodeId(null);
                                        }}
                                    />
                                ) : null}
                            </div>
                        </div>
                    </ReactFlowProvider>
                ) : (
                    <div className="flex-1 flex overflow-hidden">
                        <div className={`flex-1 transition-all duration-500 bg-card/30 ${selectedRunId ? 'max-w-md border-r border-border/50 shadow-2xl' : 'max-w-full'}`}>
                            <AutomationRuns 
                                automationId={id} 
                                onSelectRun={(runId) => setSelectedRunId(runId)} 
                            />
                        </div>
                        {selectedRunId && (
                            <div className="flex-1 min-w-0 bg-background/50 animate-in slide-in-from-right duration-500">
                                <RunInspector 
                                    runId={selectedRunId} 
                                    onClose={() => setSelectedRunId(null)} 
                                />
                            </div>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
};
const DraggableComponent = ({ item, parentType }) => {
    const onDragStart = (event) => {
        event.dataTransfer.setData('application/reactflow', parentType);
        event.dataTransfer.setData('application/reactflow-subtype', JSON.stringify({
            subtype: item.subtype,
            label: item.label
        }));
        event.dataTransfer.effectAllowed = 'move';
    };

    return (
        <div 
            className="flex flex-col p-3 rounded-xl border border-border bg-card hover:bg-primary/5 hover:border-primary/20 transition-all cursor-grab active:cursor-grabbing group shadow-[0_2px_10px_-4px_rgba(0,0,0,0.05)] hover:shadow-md ring-0 hover:ring-2 hover:ring-primary/10"
            onDragStart={onDragStart}
            draggable
        >
            <div className="flex items-center gap-3 mb-1.5">
                <div className="p-1.5 rounded-lg bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors border border-transparent group-hover:border-primary/20">
                    <item.icon className="h-3.5 w-3.5" />
                </div>
                <span className="text-xs font-black uppercase tracking-tight text-foreground group-hover:text-primary transition-colors truncate">
                    {item.label}
                </span>
            </div>
            <p className="text-[10px] font-medium text-muted-foreground/70 leading-relaxed truncate group-hover:text-muted-foreground transition-colors ml-[34px]">
                {item.desc}
            </p>
        </div>
    );
};

export default function AutomationBuilder() {
    return (
        <ReactFlowProvider>
            <AutomationBuilderShell />
        </ReactFlowProvider>
    );
}
